import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Divider, Form } from "semantic-ui-react";
import useAuth from "../../../login/AuthContext";
import {
  CreateNetwork,
  GetAllNetworksResponse,
  NetworkCreationRequest,
} from "../../../../services/NetworkApi";
import { ErrorResponse } from "../../../../services/Common";
import {
  NetworkCreationFormPieces,
  SubNetworkCreationPieces,
  UnitCreationPieces,
} from "./FormPieces";
import { useNetworkContext } from "../../../../contexts/NetworkContext/context";
import { Portfolio } from "../../../../services/PortfolioApi";

interface CreateNetworkModalProps {
  show: boolean;
  onClose: () => void;
  portfolios: Portfolio[];
}

export const CreateNetworkModal: React.FunctionComponent<CreateNetworkModalProps> = ({
  show,
  onClose,
  portfolios,
}) => {
  const [error, setError] = useState({ error: false, msg: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getAuthToken } = useAuth();
  const { loadNetworkInfoForNetworks } = useNetworkContext();

  const defaultNetworkData = {
    networkName: undefined,
    subnetworkName: undefined,
    l1Desc: undefined,
    portfolio: undefined,
    importType: undefined,
    apt: undefined,
    unitTypeName: undefined,
    unitSize: undefined,
    matchingStartDate: null,
    matchingEndDate: null,
    activationStartDate: null,
    activationEndDate: null,
  };
  const [info, setInfo] = useState<NetworkCreationRequest>(defaultNetworkData);

  const defaultErrors = {
    networkNameError: false,
    subnetworkNameError: false,
    l1DescError: false,
    importTypeError: false,
    portfolio: false,
    aptError: false,
    unitTypeNameError: false,
    unitSizeError: false,
    matchingDatesError: false,
  };

  const [errors, setErrors] = useState(defaultErrors);

  const resetData = () => {
    setInfo(defaultNetworkData);
    setErrors(defaultErrors);
    setIsSubmitting(false);
    setError({ error: false, msg: "" });
  };

  const validateFields = () => {
    const validationErrors = { ...errors };

    let errored = false;

    validationErrors.networkNameError = info.networkName == undefined;
    errored = errored || info.networkName == undefined;

    validationErrors.subnetworkNameError = info.subnetworkName == undefined;
    errored = errored || info.subnetworkName == undefined;

    validationErrors.l1DescError = info.l1Desc == undefined;
    errored = errored || info.l1Desc == undefined;

    validationErrors.importTypeError = info.importType == undefined;
    errored = errored || info.importType == undefined;

    validationErrors.portfolio = info.portfolio == undefined;
    errored = errored || info.portfolio == undefined;

    validationErrors.aptError = info.apt == undefined;
    errored = errored || info.apt == undefined;

    validationErrors.unitTypeNameError = info.unitTypeName == undefined;
    errored = errored || info.unitTypeName == undefined;

    validationErrors.unitSizeError =
      info.unitSize == undefined || info.unitSize < 1;
    errored = errored || info.unitSize == undefined || info.unitSize < 1;

    const isEveryDateIsSet = (info.matchingStartDate !== null && info.matchingEndDate !== null && info.activationStartDate !== null && info.activationEndDate !== null);
    const isEveryDateIsNull = (info.matchingStartDate === null && info.matchingEndDate === null && info.activationStartDate === null && info.activationEndDate === null);
    if(isEveryDateIsSet === false && isEveryDateIsNull === false) {
      validationErrors.matchingDatesError = true;
    }
    errored = errored || validationErrors.matchingDatesError;
    setErrors(validationErrors);
    return !errored;
  };

  const setValue = (value: string | boolean | number | null, field: string) => {
    setInfo({ ...info, [field]: value });
  };
  return (
    <Modal show={show} onHide={onClose} onExit={resetData}>
      <Modal.Header closeButton>
        <Modal.Title>Create a network</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form
          error={error.error}
          onSubmit={async (event) => {
            event.preventDefault();

            if (isSubmitting) {
              return;
            }

            if (validateFields()) {
              setIsSubmitting(true);
              setError({ error: false, msg: "" });

              try {
                const createNetwork = await CreateNetwork(
                  info,
                  await getAuthToken()
                );

                if (!createNetwork || createNetwork instanceof ErrorResponse) {
                  setError({ msg: "something went wrong", error: true });
                  return;
                }

                const response = createNetwork as GetAllNetworksResponse;
                await loadNetworkInfoForNetworks([response.networks[0].l1Id]);
                onClose();
              } catch (error) {
                setError({ msg: "something went wrong", error: true });
              } finally {
                setIsSubmitting(false);
              }
            }
          }}
        >
          <NetworkCreationFormPieces
            networkNameError={errors.networkNameError}
            l1DescError={errors.l1DescError}
            importTypeError={errors.importTypeError}
            portfolioError={errors.portfolio}
            aptError={errors.aptError}
            setValue={setValue}
            portfolios={portfolios}
          />

          <Divider />

          <SubNetworkCreationPieces
            subnetworkNameError={errors.subnetworkNameError}
            matchingDatesError={errors.matchingDatesError}
            setValue={setValue}
          />

          <Divider />

          <UnitCreationPieces
            unitTypeNameError={errors.unitTypeNameError}
            unitSizeError={errors.unitSizeError}
            setValue={setValue}
          />

          <Button
            style={{ marginTop: "5px" }}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add"}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
